/**
 * Combien d'abonnés payants pour vivre d'un SaaS, en micro-entreprise, en EURL
 * ou en SASU.
 *
 * LA QUESTION N'EST PAS LE CHIFFRE D'AFFAIRES, C'EST LE NOMBRE DE CLIENTS. Qui
 * lance un logiciel sur abonnement raisonne en abonnés, pas en euros : « il
 * m'en faut combien ? ». Le moteur remonte donc du revenu voulu au nombre
 * d'abonnés, et ne s'arrête pas au chiffre d'affaires : chaque paiement laisse
 * sa commission à Stripe, chaque euro encaissé ses cotisations, et l'impôt
 * prend sa part du bénéfice.
 *
 * UN ABONNÉ QUI PART DOIT ÊTRE REMPLACÉ. Le nombre d'abonnés ne se gagne pas
 * une fois pour toutes : chaque mois une part s'en va. Le moteur dit combien
 * en gagner chaque mois pour tenir, et, pour un rythme d'acquisition donné,
 * en combien de mois on atteint l'objectif, ou à quel plafond on s'arrête.
 *
 * EN SOCIÉTÉ, STRIPE ET LES FRAIS SE DÉDUISENT. La micro cotise sur tout ce
 * qu'elle encaisse et ne déduit rien. L'EURL et la SASU, elles, retirent la
 * commission et les frais avant de cotiser et d'être imposées : c'est
 * l'essentiel de l'écart entre les trois, avec le coût des cotisations.
 *
 * CE QUE LE MOTEUR SUPPOSE, ET QUE L'ÉCRAN DIT :
 *   - une personne seule, sans autre revenu, impôt au barème : le foyer réel
 *     se simule dans l'outil du revenu net ;
 *   - en société, tout le résultat versé en rémunération, sans dividendes, et
 *     l'EURL à l'impôt sur les sociétés, son gérant cotisant comme commerçant
 *     (scénarios du comparateur, businessStructure.js) ;
 *   - des paiements par carte européenne standard, via Stripe Billing, au
 *     tarif public ;
 *   - un prix hors taxe : sous la franchise en base, c'est aussi le prix payé ;
 *   - des abonnements mensuels, tous au même prix.
 *
 * Les taux de la micro sont ceux de microEnterprise.js, ceux des sociétés ceux
 * du comparateur : l'outil ne peut contredire ni le simulateur du revenu net,
 * ni le comparateur des statuts, sur un même chiffre d'affaires.
 */

import { PLAFONDS, SEUILS_TVA, revenuNet } from "./microEnterprise.js";
import { scenarioEurl, scenarioSasu } from "./businessStructure.js";

/**
 * Le tarif public de Stripe en France, relevé le 26/09/2026 sur
 * https://stripe.com/fr/pricing (source « stripe_tarifs », suivie par la
 * veille) : 1,5 % + 0,25 € par paiement d'une carte standard de l'Espace
 * économique européen, et 0,7 % du volume pour Stripe Billing, qui gère les
 * abonnements.
 */
export const STRIPE_CARTE_EEE = 0.015;
export const STRIPE_FIXE = 0.25;
export const STRIPE_BILLING = 0.007;

/** Les deux catégories qu'un SaaS peut déclarer en micro-entreprise. */
export const ACTIVITES_SAAS = ["services", "bnc"];

/** Les trois formes simulées. */
export const STATUTS_SAAS = ["micro", "eurl", "sasu"];

/** Au-delà, la recherche s'arrête : aucun objectif raisonnable n'y mène. */
const ABONNES_MAXIMUM = 1000000;

/** La commission de Stripe sur un paiement d'abonnement, en euros. */
export function commissionStripe(prix) {
    return prix * (STRIPE_CARTE_EEE + STRIPE_BILLING) + STRIPE_FIXE;
}

/** Ce que laisse, sur l'année, un nombre donné d'abonnés. */
export function bilanAnnuel(abonnes, { prix, statut = "micro", activite, fraisMensuels = 0 }) {
    const chiffreAffaires = abonnes * prix * 12;
    const commissions = abonnes * commissionStripe(prix) * 12;
    const frais = fraisMensuels * 12;

    if (statut === "eurl" || statut === "sasu") {
        const scenario = (statut === "eurl" ? scenarioEurl : scenarioSasu)({
            chiffreAffaires,
            charges: commissions + frais,
            activite: "services",
            artisan: false,
            foyer: {},
        });
        return {
            chiffreAffaires,
            cotisations: scenario.cotisations,
            cfp: 0,
            impot: scenario.impot,
            commissions,
            frais,
            net: scenario.net,
            remuneration: scenario.remuneration,
            taxeChambreDue: false,
            scenario,
        };
    }

    const r = revenuNet({ chiffreAffaires, activite });
    return {
        chiffreAffaires,
        cotisations: r.cotisations,
        cfp: r.cfp,
        impot: r.impot,
        commissions,
        frais,
        net: r.net - commissions - frais,
        taxeChambreDue: r.taxeChambreDue,
    };
}

/**
 * En combien de mois on atteint `cible` abonnés en partant de zéro, avec
 * `nouveaux` abonnés gagnés et `pertePct` pour cent perdus chaque mois.
 *
 * Mois après mois : abonnés = abonnés du mois d'avant × (1 − perte) + nouveaux.
 * Le compte s'approche de nouveaux ÷ perte sans jamais l'atteindre : c'est le
 * point où les départs égalent les arrivées. Le plus grand nombre entier
 * d'abonnés qu'on finit par tenir est donc juste en dessous, et `plafond` le
 * donne. Au-dessus de lui, la cible ne s'atteint pas.
 *
 * Le délai se calcule d'une formule, puis se vérifie mois par mois : une perte
 * de 0,5 % demande parfois des siècles, qu'une boucle bornée prendrait pour
 * un échec.
 */
export function progression({ cible, nouveaux, pertePct }) {
    if (cible <= 0) return { atteint: true, mois: 0, plafond: null };
    if (!(nouveaux > 0)) return { atteint: false, mois: null, plafond: 0 };
    // En pour cent jusqu'au bout : 8 ÷ 0,05 ne vaut pas 160 pour la machine.
    const limite = pertePct > 0 ? (nouveaux * 100) / pertePct : Infinity;
    const plafond = Number.isFinite(limite) ? Math.ceil(limite) - 1 : null;
    if (plafond !== null && plafond < cible) return { atteint: false, mois: null, plafond };

    const perte = pertePct / 100;
    const apres = (m) => (perte > 0 ? limite * (1 - (1 - perte) ** m) : nouveaux * m);
    let mois = perte > 0 ? Math.ceil(Math.log(1 - cible / limite) / Math.log(1 - perte)) : Math.ceil(cible / nouveaux);
    if (!Number.isFinite(mois) || mois < 1) mois = 1;
    while (mois > 1 && apres(mois - 1) >= cible - 1e-9) mois--;
    while (apres(mois) < cible - 1e-9) mois++;
    return { atteint: true, mois, plafond };
}

/**
 * Le nombre d'abonnés qu'il faut pour garder `netMensuel` chaque mois.
 *
 * @param {object} p
 * @param {number} p.prix           le prix mensuel d'un abonnement, hors taxe
 * @param {string} p.activite       « services » (BIC) ou « bnc »
 * @param {number} p.netMensuel     ce que la personne veut garder chaque mois
 * @param {number} [p.fraisMensuels] ses frais fixes par mois (hébergement, outils)
 * @param {number} p.pertePct       les abonnés perdus chaque mois, en pour cent (5 pour 5 %).
 *   En pour cent et non en fraction : 60 × 0,05 vaut 3,0000000000000004 pour
 *   la machine, et l'arrondi à l'abonné supérieur en ferait 4.
 * @param {number} [p.nouveauxParMois] les abonnés gagnés chaque mois, pour le délai
 */
export function abonnesSaas({ prix, statut = "micro", activite, netMensuel, fraisMensuels = 0, pertePct, nouveauxParMois = null }) {
    if (!STATUTS_SAAS.includes(statut)) return null;
    const micro = statut === "micro";
    if (micro && !ACTIVITES_SAAS.includes(activite)) return null;
    const parametres = { prix, statut, activite, fraisMensuels };
    const objectif = netMensuel * 12;

    const commissionParAbonne = commissionStripe(prix);
    // La société n'a pas de plafond de chiffre d'affaires.
    const plafond = micro ? PLAFONDS[activite] : null;

    // Sans marge sur la commission, aucun nombre n'atteint l'objectif : la
    // recherche s'arrête alors au maximum sans rien trouver. En société, un
    // abonné seul peut ne rien laisser tant que les frais ne sont pas couverts ;
    // c'est pourquoi on ne compare pas un abonné à zéro.
    const tientLaRoute = micro ? bilanAnnuel(1, parametres).net > bilanAnnuel(0, parametres).net : prix > commissionParAbonne;

    let abonnes = null;
    if (objectif <= bilanAnnuel(0, parametres).net) abonnes = 0;
    else if (tientLaRoute) {
        let haut = 1;
        while (haut < ABONNES_MAXIMUM && bilanAnnuel(haut, parametres).net < objectif) haut *= 2;
        if (bilanAnnuel(haut, parametres).net >= objectif) {
            let bas = 0;
            while (haut - bas > 1) {
                const milieu = Math.floor((bas + haut) / 2);
                if (bilanAnnuel(milieu, parametres).net >= objectif) haut = milieu;
                else bas = milieu;
            }
            abonnes = haut;
        }
    }

    // Le plus d'abonnés que la micro permet, et ce qu'ils laissent chaque mois.
    const abonnesAuPlafond = micro ? Math.floor(plafond / (prix * 12)) : null;
    const netMensuelMaximum = micro ? Math.max(0, bilanAnnuel(abonnesAuPlafond, parametres).net / 12) : null;

    if (abonnes === null) {
        return { possible: false, statut, commissionParAbonne, plafond, abonnesAuPlafond, netMensuelMaximum };
    }

    const bilan = bilanAnnuel(abonnes, parametres);
    const seuils = SEUILS_TVA.services;

    return {
        possible: true,
        statut,
        abonnes,
        commissionParAbonne,
        chiffreAffairesMensuel: abonnes * prix,
        ...bilan,
        netMensuelObtenu: bilan.net / 12,
        commissionsMensuelles: abonnes * commissionParAbonne,
        // Arrondi à l'abonné supérieur : un abonné de moins ne tient plus.
        nouveauxPourTenir: Math.ceil((abonnes * pertePct) / 100),
        // Pour y arriver en partant de zéro, il en faut parfois un de plus :
        // gagner exactement ceux qu'on perd au niveau visé fait tendre vers ce
        // niveau sans jamais l'atteindre (voir progression).
        nouveauxPourAtteindre: pertePct > 0 ? Math.floor((abonnes * pertePct) / 100) + 1 : 1,
        plafond,
        depassePlafond: micro && bilan.chiffreAffaires > plafond,
        abonnesAuPlafond,
        netMensuelMaximum,
        tva: {
            base: seuils.base,
            majore: seuils.majore,
            statut:
                bilan.chiffreAffaires > seuils.majore
                    ? "immediate"
                    : bilan.chiffreAffaires > seuils.base
                      ? "janvier"
                      : "franchise",
        },
        delai: nouveauxParMois === null ? null : progression({ cible: abonnes, nouveaux: nouveauxParMois, pertePct }),
    };
}
